import { useCallback } from 'react';

import { type PublicClip } from '@air/api';
import {
  type BaseCustomField,
  type Board,
  type Clip,
  type CustomField,
  CustomFieldTypeName,
  type PublicBoard,
} from '@air/api/types';
import { useAirModal } from '@air/provider-modal';

import { PrivateBulkEditDateValueCustomFieldModal } from '~/components/Modals/BulkEditCustomFieldModal/PrivateBulkEditDateValueCustomFieldModal';
import { PrivateBulkEditMultiSelectCustomFieldModal } from '~/components/Modals/BulkEditCustomFieldModal/PrivateBulkEditMultiSelectCustomFieldModal';
import { PrivateBulkEditPlainTextCustomFieldModal } from '~/components/Modals/BulkEditCustomFieldModal/PrivateBulkEditPlainTextCustomFieldModal';
import { PrivateBulkEditSingleSelectCustomFieldModal } from '~/components/Modals/BulkEditCustomFieldModal/PrivateBulkEditSingleSelectCustomFieldModal';
import { type BulkEditCustomFieldModalProps } from '~/components/Modals/BulkEditCustomFieldModal/types';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { usePrivateSelectAllTotalCount } from '~/hooks/usePrivateSelectAllTotalCount';
import { type AssignedCustomFieldValue } from '~/swr-hooks/customFields/types';

export type ShowCustomFieldBulkEditModalParams = {
  boards: (Board | PublicBoard)[];
  assets: (Clip | PublicClip)[];
  field: BaseCustomField;
};

const getCurrentValuesForCustomField = (items: (Board | PublicBoard | Clip | PublicClip)[], field: CustomField) => {
  return items.reduce((acc: AssignedCustomFieldValue[], item) => {
    const customFieldsLookup: AssignedCustomFieldValue[] = item.customFields || [];
    const currentValue = customFieldsLookup.find((cf) => cf.id === field.id);
    acc.push(currentValue ? currentValue : { value: null, values: null, ...field });
    //                                     ^
    // We want to include an empty custom field for comparison purposes.
    return acc;
  }, []);
};

export const useShowPrivateCustomFieldBulkEditModal = () => {
  const [showBulkEditSingleSelectCustomFieldModal] = useAirModal(PrivateBulkEditSingleSelectCustomFieldModal);
  const [showBulkEditMultiSelectCustomFieldModal] = useAirModal(PrivateBulkEditMultiSelectCustomFieldModal);
  const [showBulkEditPlainTextCustomFieldModal] = useAirModal(PrivateBulkEditPlainTextCustomFieldModal);
  const [showBulkEditDateValueCustomFieldModal] = useAirModal(PrivateBulkEditDateValueCustomFieldModal);
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const selectAllTotalCount = usePrivateSelectAllTotalCount();

  const showBulkEditModal = useCallback(
    ({ field, assets, boards }: ShowCustomFieldBulkEditModalParams) => {
      const boardIds = boards.map((b) => b.id);
      const assetIds = assets.map((a) => a.assetId);
      const combinedResources = [...boards, ...assets];

      const modalProps: BulkEditCustomFieldModalProps = {
        field,
        assetIds,
        boardIds,
        initialValues: getCurrentValuesForCustomField(combinedResources, field),
        totalSelectedItemsCount: selectAll ? selectAllTotalCount : undefined,
      };

      switch (field.type.title) {
        case CustomFieldTypeName.singleSelect: {
          return showBulkEditSingleSelectCustomFieldModal(modalProps);
        }
        case CustomFieldTypeName.multiSelect: {
          return showBulkEditMultiSelectCustomFieldModal(modalProps);
        }
        case CustomFieldTypeName.plainText: {
          return showBulkEditPlainTextCustomFieldModal(modalProps);
        }
        case CustomFieldTypeName.date: {
          return showBulkEditDateValueCustomFieldModal(modalProps);
        }
      }
    },
    [
      showBulkEditDateValueCustomFieldModal,
      showBulkEditMultiSelectCustomFieldModal,
      showBulkEditPlainTextCustomFieldModal,
      showBulkEditSingleSelectCustomFieldModal,
      selectAll,
      selectAllTotalCount,
    ],
  );

  return {
    showBulkEditModal,
  };
};
